import React from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts';

const FinancialCharts = () => {
  // Revenue projection data
  const revenueData = [
    { year: 'Year 1', events: 3, revenue: 3 },
    { year: 'Year 2', events: 5, revenue: 15 },
    { year: 'Year 3', events: 8, revenue: 50 }
  ];

  // GMV projection data
  const gmvData = [
    { year: 'Year 1', gmv: 0 },
    { year: 'Year 2', gmv: 225 },
    { year: 'Year 3', gmv: 750 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card className="p-3 shadow-sm">
          <p className="text-sm font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm text-muted-foreground">
              {entry.name}: ${entry.value}M
            </p>
          ))}
        </Card>
      );
    }
    return null;
  };

  return (
    <section id="financials" className="py-16 lg:py-24 bg-secondary/30">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="uppercase tracking-[0.22em] text-xs text-muted-foreground mb-6">
            Financial Projections
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
            Path to Scale
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Network-driven revenue in Year 1, platform GMV scaling rapidly in Years 2-3 as trust infrastructure matures.
          </p>
        </motion.div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Revenue Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Revenue Projection</h3>
              <div className="chart-container" data-testid="financials-revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.12} />
                        <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="year"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickFormatter={(value) => `$${value}M`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke="hsl(var(--accent))"
                      strokeWidth={2}
                      fill="url(#colorRevenue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </motion.div>

          {/* GMV Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Platform GMV (Relic)</h3>
              <div className="chart-container" data-testid="financials-gmv-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={gmvData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="year"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickFormatter={(value) => `$${value}M`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="gmv"
                      name="GMV"
                      stroke="hsl(var(--primary))"
                      strokeWidth={3}
                      dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Investment Ask */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="p-8 bg-primary text-primary-foreground">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-semibold mb-6 text-center">Investment Opportunity</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-3xl font-bold tabular-nums mb-2">$2.5M</p>
                  <p className="text-sm text-primary-foreground/80">Seed Round</p>
                </div>
                <div>
                  <p className="text-3xl font-bold tabular-nums mb-2">$15M–$20M</p>
                  <p className="text-sm text-primary-foreground/80">Pre-Money Valuation</p>
                </div>
                <div>
                  <p className="text-3xl font-bold tabular-nums mb-2">Q2 2026</p>
                  <p className="text-sm text-primary-foreground/80">Target Close</p>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-primary-foreground/20">
                <p className="text-sm text-center text-primary-foreground/80">
                  Capital allocated to: Relic platform development • AI authentication layer • Event expansion • Strategic hires
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default FinancialCharts;
